import axios from "axios";
import i18n from "i18next";
import { startLoading, stopLoading } from "../actions/loading";
import { notify } from "../actions/notification";
import { resetStore } from "../actions/user";
import { resetTimer } from "../actions/timer";

export const baseURL = process.env.REACT_APP_API_URL;
const api = axios.create({
  baseURL,
  timeout: 60000,
  withCredentials: true,
});

let openReqs = 0;

export function initInterceptors(store) {
  const requestInterceptor = (config) => {
    if (config.hideLoader) return config;

    if (openReqs === 0) {
      store.dispatch(startLoading());
    }
    openReqs++;
    return config;
  };

  const requestErrorHandler = (err) => {
    openReqs--;
    if (openReqs === 0) {
      store.dispatch(stopLoading());
    }
    return Promise.reject(err);
  };

  // Apply interceptor
  api.interceptors.request.use(requestInterceptor, requestErrorHandler);

  // Response interceptor
  const responseInterceptor = (response) => {
    openReqs--;
    if (openReqs === 0) {
      store.dispatch(stopLoading());
    }

    // Get session expiration from the non-HttpOnly cookie
    const cookies = document.cookie.split(";");
    const sessionExpiryCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("X-Session-Expires=")
    );

    if (sessionExpiryCookie) {
      const expiresAt = parseInt(sessionExpiryCookie.split("=")[1], 10);
      if (!isNaN(expiresAt) && expiresAt > Date.now()) {
        store.dispatch(resetTimer(expiresAt));
      }
    } else {
      // fallback: reset timer with default value
      store.dispatch(resetTimer());
    }

    // Handle success messages
    if (response && response.data && response.data.message) {
      store.dispatch(
        notify({
          type: "success",
          message: response.data.message,
        })
      );
    }

    return response;
  };

  const responseErrorHandler = (err) => {
    openReqs--;
    if (openReqs === 0) {
      store.dispatch(stopLoading());
    }

    if (err.config?.skipErrorNotification) {
      return Promise.reject(err);
    }

    // Display the error message from the API or a fallback message in case the API returns with no message
    const data = err.response?.data;
    const hasData = data && Object.keys(data).length;
    const message = hasData
      ? data.message
      : i18n.t("Oops, something bad happened!");

    if (hasData) {
      // the isJoi field is returned from the backend for historic reasons
      // we only need to know which fields are invalid, we don't need to know if it was validated by joi
      delete err.response.data.isJoi;
    }
    // return early if the API returned with a bunch of field-level, specific error messages
    // there is no need to show a generic fallback error message in this case
    if (!message) {
      throw err;
    }

    if (message.indexOf("not found in Db") !== -1) {
      throw err;
    }
    // Log out if the session is expired (they API send a "Please log in" message in this case)
    if (message.indexOf("Please log in") !== -1) {
      // this simply removed the user from the store to redirect to the login screen
      // a fully API logout is unnecessary since the session is already expired
      // it would also lead to an async infinite loop since it would throw a 400 type "Please login" error
      store.dispatch(resetStore());
      store.dispatch(
        notify({
          type: "error",
          message: i18n.t("Your session expired! Please log in again."),
        })
      );
    } else {
      // display the error message as a notification
      store.dispatch(
        notify({
          type: "error",
          message,
        })
      );
    }
    throw err;
  };

  // Apply response interceptor
  api.interceptors.response.use(responseInterceptor, responseErrorHandler);
}

export default api;
